import { type FormEvent, useState } from "react";

import {
  useErrors,
  usePasswordValidation,
  useResetPassword,
  useScreen,
} from "@auth0/auth0-acul-react/reset-password";
import { AlertCircle } from "lucide-react";

import CareSuperLogo from "@/components/CareSuperLogo";
import PasswordInput from "@/components/universal-login/PasswordInput";
import PasswordPolicyChecklist from "@/components/universal-login/PasswordPolicyChecklist";
import UniversalLoginWidget from "@/components/universal-login/UniversalLoginWidget";
import { executeSafely } from "@/utils/helpers/executeSafely";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";

import locales from "./locales/en.json";

/**
 * reset-password: "Change Your Password", the screen a reset link lands on.
 *
 * TENANT-WIDE. Serves RO-A activation (the "Welcome to CareSuper" email), the
 * in-portal reset, and the login page's "Forgot password?" code journey on
 * BOTH member portals. That's why this is a like-for-like replica of the
 * hosted screen, measured off it, not a CareSuper-journey restyle: anything
 * that looked different here would look different to every member who
 * resets.
 *
 * NOTHING HERE TOUCHES THE WELCOME EMAIL. The RO-A copy is selected in the
 * reset_email Liquid template by application.clientID (the RO branch-key
 * client sent by registerOnline.js). This screen renders after the email has
 * been sent and clicked. It reads the client only for the "Back to" label on
 * the following screen.
 *
 * Copy: Auth0's screen.texts when present (honours custom text and locale),
 * else the strings captured live in locales/en.json.
 * Policy: from transaction.passwordPolicy via usePasswordValidation, never
 * hard-coded.
 */
function ResetPasswordScreen() {
  const resetPassword = useResetPassword();
  const screen = useScreen();
  const { errors } = useErrors();

  applyAuth0Theme(resetPassword);

  const texts = (screen.texts || {}) as Record<string, string | undefined>;
  if (texts.pageTitle) document.title = texts.pageTitle;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { isValid: policyValid, results: rules } =
    usePasswordValidation(password);

  // Hosted behaviour, observed live: once the new password is non-empty and
  // fails the policy, BOTH fields go red, not just the first.
  const policyInvalid = password.length > 0 && !policyValid;

  const serverErrors = errors.byType("auth0");
  const fieldError =
    errors.byField("password-reset")[0]?.message ||
    errors.byField("re-enter-password")[0]?.message;
  const generalError = serverErrors.find((e) => !e.field)?.message;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password || !policyValid) return;
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setSubmitting(true);
    await executeSafely("Reset password", () =>
      resetPassword.resetPassword({
        "password-reset": password,
        "re-enter-password": confirm,
      })
    );
    setSubmitting(false);
  };

  const errorText =
    (mismatch && (texts.passwordsMismatch || locales.mismatch)) ||
    fieldError ||
    generalError;

  return (
    <UniversalLoginWidget>
      {/* Header: padding 40 40 24, logo 52px, title 24px below it. */}
      <header className="flex flex-col items-center px-10 pt-10 pb-6 text-center">
        <CareSuperLogo className="h-[52px] w-auto self-center" />
        <h1
          className="m-0 mt-6 mb-4"
          style={{
            fontSize: "var(--ul-theme-font-title-size, 1.5rem)",
            lineHeight: "36px",
            // The theme's title.bold (true) arrives as this variable.
            fontWeight:
              "var(--ul-theme-font-title-weight, 700)" as React.CSSProperties["fontWeight"],
            color: "var(--ul-theme-color-header, #1d2b4f)",
          }}
        >
          {texts.title || locales.title}
        </h1>
        <p
          className="m-0"
          style={{
            fontSize: "var(--ul-theme-font-subtitle-size, 0.875rem)",
            lineHeight: 1.5,
            color: "var(--ul-theme-color-body-text, #4b5563)",
          }}
        >
          {texts.description || locales.description}
        </p>
      </header>

      <form
        className="flex flex-col px-10 pb-10"
        onSubmit={onSubmit}
        noValidate
      >
        <PasswordInput
          name="password-reset"
          label={
            texts.passwordPlaceholder
              ? `${texts.passwordPlaceholder}*`
              : locales.passwordLabel
          }
          autoComplete="new-password"
          autoFocus
          value={password}
          invalid={policyInvalid || !!errorText}
          showLabel={locales.showPassword}
          hideLabel={locales.hidePassword}
          onChange={(e) => {
            setPassword(e.target.value);
            setMismatch(false);
          }}
        />
        <PasswordInput
          name="re-enter-password"
          className="mt-[14px]"
          label={
            texts.reEnterpasswordPlaceholder
              ? `${texts.reEnterpasswordPlaceholder}*`
              : locales.reEnterPasswordLabel
          }
          autoComplete="new-password"
          value={confirm}
          invalid={policyInvalid || !!errorText}
          showLabel={locales.showPassword}
          hideLabel={locales.hidePassword}
          onChange={(e) => {
            setConfirm(e.target.value);
            setMismatch(false);
          }}
        />

        {errorText ? (
          <div
            role="alert"
            className="mt-2 flex items-center gap-1"
            style={{
              color: "var(--ul-theme-color-error, #d03c38)",
              fontSize: "var(--ul-theme-font-body-text-size, 0.875rem)",
              lineHeight: "20px",
            }}
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorText}</span>
          </div>
        ) : null}

        {password.length > 0 ? (
          <PasswordPolicyChecklist
            title={texts.passwordSecurityText || locales.passwordSecurityText}
            rules={rules}
          />
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="mt-6 flex h-[52px] w-full cursor-pointer items-center justify-center px-4 rounded-[var(--ul-theme-border-button-border-radius,9999px)] hover:shadow-[inset_0_0_0_150px_rgba(0,0,0,0.1)] focus:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:opacity-70"
          style={{
            backgroundColor: "var(--ul-theme-color-primary-button, #294fed)",
            color: "var(--ul-theme-color-primary-button-label, #ffffff)",
            fontSize: "var(--ul-theme-font-buttons-text-size, 1rem)",
          }}
        >
          {texts.buttonText || locales.buttonText}
        </button>
      </form>
    </UniversalLoginWidget>
  );
}

export default ResetPasswordScreen;
